import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StaticSandboxApp from '../StaticSandboxApp';

import ChampionHeader from '../../components/ChampionHeader/ChampionHeader';
import ChampionNotFound from './ChampionNotFound';
import './StaticChampionTracker.css';
//import "./ChampionTracker.css"

import { ChampionConfig } from '../types/staticTypes';
import BuildInfo from '../../components/BuildInfo/BuildInfo';
import DataService from '../../XXX/DataService';
import { useChampionDisplayNames } from '../../utils/championNames';

const StaticChampionTracker: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const championName = name || 'Unknown';
  
  const [championData, setChampionData] = useState<any>(null);
  const [championTitle, setChampionTitle] = useState<string | null>(null);
  const [currentOpponent, setCurrentOpponent] = useState<string>('');
  const [championLane, setChampionLane] = useState<string>('');
  const [opponentLoaded, setOpponentLoaded] = useState(false);
  const [apiVersion, setApiVersion] = useState<string>('15.23.1');
  const [championValid, setChampionValid] = useState<boolean | null>(null); // null = loading
  const { getDisplayName } = useChampionDisplayNames();
  
  // Fetch latest API version and validate champion name
  useEffect(() => {
    DataService.fetchLatestVersion().then(setApiVersion);
  }, []);

  // Validate champion name against ddragon champion list
  useEffect(() => {
    if (!championName || championName === 'Unknown') {
      setChampionValid(false);
      return;
    }

    const validateChampion = async () => {
      try {
        const version = await DataService.fetchLatestVersion();
        const response = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`
        );
        const data = await response.json();
        const normalize = (s: string) => s.toLowerCase().replace(/['\.\s-]/g, '');
        const championIds = Object.keys(data.data).map(id => normalize(id));
        const championNames = Object.values(data.data).map((c: any) => normalize(c.name));

        const searchNormalized = normalize(championName);
        const isValid = championIds.includes(searchNormalized) || championNames.includes(searchNormalized);
        setChampionValid(isValid);
      } catch (error) {
        console.error('Error validating champion name:', error);
        // On error, allow it through (benefit of the doubt)
        setChampionValid(true);
      }
    };

    validateChampion();
  }, [championName]);

  // Set default opponent from database (most common lane matchup)
  useEffect(() => {
    if (!championName || championName === 'Unknown' || championValid === false) return;

    const fetchDefaultOpponent = async () => {
      setOpponentLoaded(false);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${championName}/default-opponent`
        );
        if (res.ok) {
          const json = await res.json();
          if (json.success && json.data?.opponent) {
            setCurrentOpponent(json.data.opponent);
            if (json.data.lane) setChampionLane(json.data.lane);
            setOpponentLoaded(true);
            return;
          }
        }
      } catch (err) {
        console.warn('Could not fetch default opponent, falling back:', err);
      }
      // Fallback if API fails
      setCurrentOpponent(championName === 'Garen' ? 'Ahri' : 'Garen');
      setOpponentLoaded(true);
    };

    fetchDefaultOpponent();
  }, [championName, championValid]);

  // Fetch champion stats data from backend
  useEffect(() => {
    if (championName === 'Unknown' || championValid !== true) return;

    const fetchStats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/champion/${championName}/stats`
        );
        if (response.ok) {
          const data = await response.json();
          setChampionData(data);
        }
      } catch (error) {
        console.error('Error fetching champion stats:', error);
      }
    };

    fetchStats();
  }, [championName, championValid]);

  // Fetch champion title from Riot API (independent of backend)
  useEffect(() => {
    if (championName === 'Unknown' || championValid !== true) return;

    const fetchTitle = async () => {
      try {
        const championHeaderData = await DataService.fetchChampionHeaderData(championName);
        setChampionTitle(championHeaderData.title);
      } catch (error) {
        console.error('Error fetching champion title:', error);
      }
    };

    fetchTitle();
  }, [championName, championValid]);

  // Handle opponent change from grid
  const handleOpponentChange = (opponent: string) => {
    console.log('Opponent changed in StaticChampionTracker:', opponent);
    setCurrentOpponent(opponent);
  };

  // Handle export to damage calculator
  const handleExportToDamageCalculator = (mainChampion: ChampionConfig, targetChampion: ChampionConfig) => {
    console.log('[StaticChampionTracker] Received export request:', { mainChampion, targetChampion });
    
    // Store the configurations in sessionStorage for the new tab to access
    const exportData = {
      mainChampion,
      targetChampion,
      fromStaticTracker: true,
      timestamp: Date.now()
    };
    
    console.log('[StaticChampionTracker] Storing export data in sessionStorage:', exportData);
    const exportDataString = JSON.stringify(exportData);
    sessionStorage.setItem('damageCalculatorExport', exportDataString);
    
    // Also store in localStorage as backup
    localStorage.setItem('damageCalculatorExport', exportDataString);
    
    // Verify the data was stored
    const storedSessionData = sessionStorage.getItem('damageCalculatorExport');
    const storedLocalData = localStorage.getItem('damageCalculatorExport');
    console.log('[StaticChampionTracker] Verified stored data:', {
      sessionStorage: storedSessionData,
      localStorage: storedLocalData
    });
    
    // Small delay to ensure data is stored before opening new tab
    setTimeout(() => {
      // Open damage calculator in new tab
      window.open('/damage-calculator', '_blank');
    }, 100);
  };

  // Generate champion image URL
  const championImageUrl = championName !== 'Unknown' 
    ? `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${championName}.png`
    : '';

  // Still validating
  if (championValid === null) {
    return (
      <div className="static-champion-tracker">
        <div className="loading-opponent">Loading...</div>
      </div>
    );
  }

  // Champion not found
  if (championValid === false) {
    return <ChampionNotFound searchedName={championName} />;
  }

  // Use the proper display name from Riot API (e.g., "Kaisa" -> "Kai'Sa", "MonkeyKing" -> "Wukong")
  const displayName = getDisplayName(championName);

  return (
    <div className="static-champion-tracker">
      <Helmet>
        <title>{displayName} – LoL Calculator | Damage, Builds & Matchups</title>
        <meta name="description" content={`${displayName} damage calculator for League of Legends. Simulate matchups, compare item builds, analyze combos, and find the best runes for ${displayName}.`} />
        <link rel="canonical" href={`https://lolcalculator.gg/champions/${championName}`} />
        <meta property="og:title" content={`${displayName} – LoL Calculator`} />
        <meta property="og:description" content={`${displayName} damage calculator. Simulate matchups, compare item builds, and analyze combos.`} />
        <meta property="og:url" content={`https://lolcalculator.gg/champions/${championName}`} />
        <meta property="og:image" content={`https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${championName}.png`} />
      </Helmet>

      {/* Champion Header - separated */}
      <ChampionHeader 
        championName={championName}
        championImageUrl={championImageUrl}
        championTitle={championTitle || undefined}
        championData={championData}
      />
      
      {/* Combined content section - BuildInfo + StaticSandboxApp */}
      <div className="champion-content-combined">
        <BuildInfo championName={championName} championLane={championLane} />
        
        {opponentLoaded ? (
          <StaticSandboxApp 
            championName={championName} 
            defaultOpponent={currentOpponent}
            defaultLane={championLane}
            onExportToDamageCalculator={handleExportToDamageCalculator}
            onOpponentChange={handleOpponentChange}
          />
        ) : (
          <div className="loading-opponent">Loading matchup data...</div>
        )}
      </div>
    </div>
  );
};

export default StaticChampionTracker; 